import type { SxProps, Theme } from "@mui/material";
import { Box, Switch, Typography, useTheme } from "@mui/material";
import deepEqual from "fast-deep-equal";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import type { ConditionFilterDescriptor } from "../../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  ConditionField,
  DraggableFieldType,
  DraggableMetaType,
  FieldWrapper,
} from "../../../../../../../../../api/types/svTypes.ts";
import { AreaItemType, ItemDataType } from "../../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch";
import { selectAutoShowItemOptionsId } from "../../../../../store/selectors/currentReportSelectors.ts";
import {
  selectBuilderDetailedView,
  selectConditions,
  selectReportType,
} from "../../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks";
import { createCurrentReportAction } from "../../../../../store/utils/storeUtils.ts";
import type { ShowFieldOptionsSettings } from "../../../../viewer/common/Types.ts";
import ConditionFieldOptionPopup from "../../../../viewer/common/conditions/ConditionFieldOptionPopup.tsx";
import ConditionItem from "../../../../viewer/common/conditions/ConditionItem.tsx";
import { formatCaption } from "../../../../viewer/common/conditions/conditionsHelper.tsx";
import type { FieldItemProps } from "../../../../viewer/common/fields/area-fields/areaFiledItem.types.ts";
import { getFiltersToApply } from "../../../../viewer/common/hooks/getFiltersToApply.ts";
import type { AddFieldAction } from "../../../common/fields/DropFieldContainer.tsx";
import DropFieldContainer from "../../../common/fields/DropFieldContainer.tsx";
import { createConditionField } from "../../../common/utilities/createFields.ts";
import { extractGuid, extractMeta } from "../../../common/utilities/dropFieldContainerHelper.ts";
import { canDropConditionItem } from "./canDropConditionItem.ts";

interface Props {
  readonlyMode?: boolean;
  containerSx?: SxProps<Theme>;
  disableEqualitySelection?: boolean;
  disableReferenceDateSelection?: boolean;
  addFieldAction?: AddFieldAction;
  renderCustomId?: (field: ConditionField) => JSX.Element;
}

export default function ConditionsDropFieldsContainer({
  readonlyMode,
  containerSx,
  disableEqualitySelection,
  disableReferenceDateSelection,
  addFieldAction,
  renderCustomId,
}: Props) {
  const dispatch = useAppDispatch();
  const reportType = useSelector(selectReportType) ?? undefined;
  const conditionsAreaValues = useSelector(selectConditions);

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [optionField, setOptionField] = useState<ConditionField>();
  const detailedView = useSelector(selectBuilderDetailedView);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);
  const narrowingFilters = useMemo(
    () => (optionField === undefined ? [] : getFiltersToApply(conditionsAreaValues, optionField)),
    [conditionsAreaValues, optionField]
  );

  const onSetAutoShowItemOptionsId = useCallback(
    (id?: string) => {
      dispatch(createCurrentReportAction("setAutoShowItemOptionsId", id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (optionField !== undefined) {
      const field = conditionsAreaValues.find((f) => f.config.guid === optionField.config.guid);
      if (field !== undefined) {
        // TODO #209
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptionField(field);
      }
    }
  }, [conditionsAreaValues, optionField]);

  const handleClosePopup = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const onShowOptions = useCallback((settings: ShowFieldOptionsSettings<ConditionField>) => {
    setOptionField(settings.field);
    setAnchorEl(settings.ref);
    setShowOnlySelected(settings.showOnlySelected);
  }, []);

  const canDropItem = useCallback(
    (item: FieldWrapper<DraggableFieldType> | DraggableMetaType) => canDropConditionItem(item, reportType),
    [reportType]
  );

  const onFilterUpdated = useCallback(
    (filter: ConditionFilterDescriptor) => {
      const field = conditionsAreaValues.find((value) => value.config.guid === optionField?.config.guid);
      if (field && !deepEqual(field.config.filter, filter)) {
        dispatch(fieldsStateActions.updateCondition({ field, changes: { config: { ...field.config, filter } } }));
      }
      handleClosePopup();
    },
    [dispatch, optionField, handleClosePopup, conditionsAreaValues]
  );

  const onUpdateAsParameter = useCallback(
    (field: ConditionField, newValue: boolean) => {
      dispatch(fieldsStateActions.updateConditionConfig({ field, changes: { ...field.config, parameter: newValue } }));
    },
    [dispatch]
  );

  const onUpdateAsMandatory = useCallback(
    (field: ConditionField, newValue: boolean) => {
      dispatch(fieldsStateActions.updateConditionConfig({ field, changes: { ...field.config, mandatory: newValue } }));
    },
    [dispatch]
  );

  const onUpdateCustomLabel = useCallback(
    (customLabel: string | undefined) => {
      if (optionField) {
        dispatch(
          fieldsStateActions.updateCondition({
            field: optionField,
            changes: { config: { ...optionField.config, customLabel: customLabel } },
          })
        );
      }
    },
    [dispatch, optionField]
  );

  const onCreateItem = useCallback(
    (props: FieldItemProps<ConditionField>) => {
      return (
        <ConditionItem
          field={props.field}
          linked={props.field.hasLinks}
          marked={props.field.config.parameter}
          canBeRemoved={props.canBeRemoved}
          onRemoveItem={props.onRemoveItem}
          formatCaption={props.formatCaption}
          optionRef={props.optionRef}
          onShowOptions={props.onShowOptions}
          autoShowItemOptionsId={autoShowItemOptionsId}
          onSetAutoShowItemOptionsId={onSetAutoShowItemOptionsId}
          readonly={readonlyMode ?? false}
        />
      );
    },
    [autoShowItemOptionsId, onSetAutoShowItemOptionsId, readonlyMode]
  );

  const onFormatCaption = useCallback(
    (field: ConditionField): JSX.Element => {
      return formatCaption(
        field,
        field.validation?.invalid ? theme.palette.text.secondary : undefined,
        disableEqualitySelection
      );
    },
    [theme.palette.text.secondary, disableEqualitySelection]
  );

  const removeItem = useCallback(
    (item: ConditionField) => {
      dispatch(fieldsStateActions.removeCondition(item));
    },
    [dispatch]
  );

  const moveItem = useCallback(
    (field: ConditionField, newIndex: number) => {
      dispatch(fieldsStateActions.moveCondition({ field, newIndex }));
    },
    [dispatch]
  );

  const addItem = useCallback(
    (field: ConditionField, index: number) => {
      dispatch(fieldsStateActions.addCondition({ field, index }));
    },
    [dispatch]
  );
  const handleOnRemoveItem = useCallback(
    (item: ConditionField): void => {
      removeItem(item);
      handleClosePopup();
    },
    [removeItem, handleClosePopup]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
      <DropFieldContainer
        title="Conditions"
        headingActions={
          <>
            <Typography>Detailed view</Typography>
            <Switch
              checked={detailedView}
              disabled={readonlyMode}
              onChange={(_, value) =>
                dispatch(fieldsStateActions.updateSettings({ settings: { detailedView: value } }))
              }
            />
          </>
        }
        areaFieldType={AreaItemType.CONDITIONS}
        acceptedDropTypes={[
          AreaItemType.DIMENSION,
          AreaItemType.FIELDS,
          AreaItemType.COLUMNS,
          AreaItemType.ROWS,
          AreaItemType.ARGUMENTS,
          AreaItemType.VALUES,
        ]}
        fields={conditionsAreaValues}
        readonly={readonlyMode}
        addFieldAction={addFieldAction}
        containerSx={containerSx}
        getKeyValue={getKeyValue}
        renderListItem={onCreateItem}
        formatCaption={onFormatCaption}
        onAddItem={addItem}
        onRemoveItem={removeItem}
        onMoveItem={moveItem}
        showOptions={onShowOptions}
        createListItem={createConditionField}
        canDropItem={canDropItem}
      />
      {anchorEl && optionField && (
        <ConditionFieldOptionPopup
          anchorEl={anchorEl}
          field={optionField}
          narrowingFilters={narrowingFilters}
          showParameter={!readonlyMode}
          showOnlySelectedByDefault={showOnlySelected}
          updateParameter={onUpdateAsParameter}
          updateMandatory={onUpdateAsMandatory}
          updateCustomLabel={onUpdateCustomLabel}
          save={onFilterUpdated}
          onRemoveItem={handleOnRemoveItem}
          canBeRemoved={!readonlyMode}
          resizable={optionField.meta.type !== ItemDataType.Date}
          disableEqualitySelection={disableEqualitySelection}
          disableReferenceDateSelection={disableReferenceDateSelection}
          CustomId={renderCustomId?.(optionField)}
          readonly={readonlyMode}
          readonlyCustomLabel={readonlyMode}
        />
      )}
    </Box>
  );
}

function getKeyValue(field: FieldWrapper<DraggableFieldType> | DraggableMetaType) {
  const guid = extractGuid(field);
  if (guid) {
    return guid;
  }
  const meta = extractMeta(field);
  if (meta) {
    return meta.name;
  }
  return "";
}
